import React, { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { FaFilter } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import { useGetProductListQuery } from "../../utils/api/product/product.api";
import { BestSellerCard } from "../HomePage/BestSellerCard";
import FrontFooter from "../shared/FrontFooter/FrontFooter";
import FrontHeader from "../shared/FrontHeader/FrontHeader";
import FilterItems from "../user/ParcoursAchat/FilterItems";
import FilterMobileItem from "../user/ParcoursAchat/modal/FilterMobileItem";
import { ProductItemsSkeleton } from "../user/ParcoursAchat/ParcoursAchat";

function safeJsonParse<T>(value: string | null | undefined, fallback: T): T {
  if (!value) return fallback;
  try {
    const parsed = JSON.parse(value);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

/** Aligné sur la classe CSS `no-view-desktop` (768px) */
const MOBILE_BREAKPOINT = 768;

function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

let options = [
  {
    label: "Nombre d'avis",
    value: "avreageRate",
  },
  {
    label: "Date de création",
    value: "createdAt",
  },
  {
    label: "Nom",
    value: "name",
  },
  {
    label: "Note moyenne",
    value: "rateCount",
  },
  {
    label: "Prix",
    value: "price",
  },
];

function OurProduct() {
  const limit = 12;
  const [page, setPage] = useState(1);
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const observerTarget = useRef<HTMLDivElement>(null);
  let [searchParams, setSearchParams] = useSearchParams();
  const [show, setShow] = useState(false);
  const isMobile = useIsMobile();
  const handleShow = () => setShow(true);
  const [query, setQuery] = React.useState({
    categorie: searchParams?.get("categorie") || "",
    sous_categorie: searchParams?.get("sous_categorie") || "",
    prix_min: searchParams?.get("min") || "",
    prix_max: searchParams?.get("max") || "",
    orderBy: searchParams?.get("orderBy") || "",
  });

  // Desktop : filtres categorie/sous_categorie appliqués.
  // Mobile : catalogue complet (seul le prix reste filtré via searchParams).
  const { data = { results: [], count: 0 }, isFetching } =
    useGetProductListQuery({
      page,
      limit,
      prix_min: searchParams?.get("prix_min") || "",
      prix_max: searchParams?.get("prix_max") || "",
     categorie: searchParams?.get("categorie") || "",
sous_categorie: safeJsonParse<string[]>(searchParams?.get("sous_categorie"), []),
    });

  // Réinitialiser les produits quand les filtres (ou le mode mobile) changent
  useEffect(() => {
    setAllProducts([]);
    setPage(1);
    setHasMore(true);
  }, [
    searchParams?.get("categorie"),
    searchParams?.get("sous_categorie"),
    searchParams?.get("prix_min"),
    searchParams?.get("prix_max"),
    isMobile,
  ]);

  // Accumuler les produits
  useEffect(() => {
    if (data?.results) {
      if (page === 1) {
        setAllProducts(data.results);
        setHasMore(data.results.length === limit && data.count > data.results.length);
      } else {
        setAllProducts((prev) => {
          const newProducts = [...prev, ...data.results];
          setHasMore(newProducts.length < data.count && data.results.length === limit);
          return newProducts;
        });
      }
    }

    if (data?.results && data.results.length === 0 && page === 1) {
      setAllProducts([]);
      setHasMore(false);
    }
  }, [data?.results, data?.count, page, limit]);

  // Observer pour le scroll infini
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetching) {
          setPage((prevPage) => prevPage + 1);
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, isFetching]);

  return (
    <>
      <div className="landing-page-component">
        <FrontHeader />
        <div className="page-content">
          <div className="container-easy-market">
            <div className="row row-achat-page mb-5">
              <FilterItems
                searchParams={searchParams}
                setSearchParams={setSearchParams}
                query={query}
                setQuery={setQuery}
                setPage={() => {
                  setPage(1);
                  setAllProducts([]);
                  setHasMore(true);
                }}
              />
              <div className="container-filter-parcours-achat-mobile no-view-desktop">
                <button
                  className="btn btn-filter-mobile"
                  type="button"
                  onClick={() => handleShow()}
                >
                  <FaFilter />
                  <span className="ps-2">Filtrer</span>
                </button>
              </div>

              <div className="col-lg-9 col-md-7 col-right-achat-page">
                <div className="content-col-right-achat-page">
                  <div
                    className="container-home-page mb-3 our-product-container-homepage"
                    style={{ minHeight: "51rem" }}
                  >
                    <Row className="mt-3">
                      {page === 1 && !!isFetching && <ProductItemsSkeleton />}
                      {!!allProducts?.length &&
                        allProducts.map((product, key) => {
                          return (
                            <Col
                              sm={6}
                              xs={6}
                              md={4}
                              lg={4}
                              className="mb-3 col-product-item d-flex col-content-product-home"
                              key={product?.id || key}
                              style={{
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <div className="product-item-carousel">
                                <BestSellerCard item={product} key={product?.id || key} />
                              </div>
                            </Col>
                          );
                        })}
                      {page === 1 && !isFetching && allProducts.length === 0 && (
                        <div className="col-sm-12 col-md-12 mb-3 m-t-36 d-flex justify-content-center">
                          <p>Aucun produit disponible </p>
                        </div>
                      )}
                    </Row>

                    {/* Élément sentinelle pour le scroll infini */}
                    {hasMore && (
                      <div ref={observerTarget} style={{ height: "20px", marginTop: "20px" }}>
                        {isFetching && page > 1 && (
                          <div className="d-flex justify-content-center">
                            <ProductItemsSkeleton />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* <FooterPage /> */}
          </div>
        </div>
        <FrontFooter />
        <FilterMobileItem
          show={show}
          setShow={setShow}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          query={query}
          setQuery={setQuery}
          setPage={() => {
            setPage(1);
            setAllProducts([]);
            setHasMore(true);
          }}
        />
      </div>
    </>
  );
}

export default OurProduct;